import { useEffect } from 'react'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Search, Users, Calendar, Home, Trophy, Heart, Mail, Sun, Moon, GraduationCap } from 'lucide-react'
import { useUiStore } from '@/stores/ui'
import { usePrograms, useCoaches } from '@/lib/hooks'
import { cn } from '@/lib/utils'

export function CommandPalette() {
  const { paletteOpen, setPaletteOpen, togglePalette } = useUiStore()
  const navigate = useNavigate()
  const { resolvedTheme, setTheme } = useTheme()
  const { data: programs = [] } = usePrograms()
  const { data: coaches = [] } = useCoaches()

  useEffect(() => {
    function down(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        togglePalette()
      }
      if (e.key === 'Escape') setPaletteOpen(false)
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [togglePalette, setPaletteOpen])

  if (!paletteOpen) return null

  function go(path: string) {
    navigate(path)
    setPaletteOpen(false)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[15vh] bg-pitch-950/70 backdrop-blur-sm"
      onClick={() => setPaletteOpen(false)}
      role="dialog"
      aria-modal="true"
    >
      <Command
        className="w-full max-w-xl rounded-lg border border-border bg-card shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center border-b border-border px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Command.Input
            autoFocus
            placeholder="Search programs, coaches, actions…"
            className="flex-1 h-14 bg-transparent px-3 text-sm text-foreground focus:outline-none placeholder:text-muted-foreground"
          />
          <kbd className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">Esc</kbd>
        </div>
        <Command.List className="max-h-[400px] overflow-y-auto p-2">
          <Command.Empty className="py-8 text-center text-sm text-muted-foreground">
            No matches. Try "recreation", "elite", "donate"…
          </Command.Empty>

          <Group heading="Navigate">
            <Item icon={<Home className="h-4 w-4" />} label="Home" onSelect={() => go('/')} />
            <Item icon={<Users className="h-4 w-4" />} label="Teams" onSelect={() => go('/teams')} />
            <Item icon={<Calendar className="h-4 w-4" />} label="Camps" onSelect={() => go('/camps')} />
            <Item icon={<Trophy className="h-4 w-4" />} label="Programs" onSelect={() => go('/programs')} />
            <Item icon={<Mail className="h-4 w-4" />} label="Contact" onSelect={() => go('/contact')} />
          </Group>

          <Group heading="Actions">
            <Item icon={<Users className="h-4 w-4" />} label="Register a player" onSelect={() => go('/register')} />
            <Item icon={<GraduationCap className="h-4 w-4" />} label="Apply for scholarship" onSelect={() => go('/scholarship')} />
            <Item icon={<Heart className="h-4 w-4" />} label="Donate" onSelect={() => go('/donate')} />
            <Item
              icon={resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} theme`}
              onSelect={() => {
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
                setPaletteOpen(false)
              }}
            />
          </Group>

          <Group heading="Programs">
            {programs.map((p) => (
              <Item key={p.slug} icon={<Trophy className="h-4 w-4" />} label={p.name} onSelect={() => go(`/programs/${p.slug}`)} />
            ))}
          </Group>

          <Group heading="Coaches">
            {coaches.map((c) => (
              <Item key={c.slug} icon={<Users className="h-4 w-4" />} label={c.name} onSelect={() => go(`/coaches`)} />
            ))}
          </Group>
        </Command.List>
      </Command>
    </div>
  )
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group heading={heading} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
      {children}
    </Command.Group>
  )
}

function Item({ icon, label, onSelect }: { icon: React.ReactNode; label: string; onSelect: () => void }) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors',
        'data-[selected=true]:bg-cosmos-green/10 data-[selected=true]:text-cosmos-green'
      )}
    >
      {icon}
      <span>{label}</span>
    </Command.Item>
  )
}
